import { Component } from '@angular/core';
import { FormControl, Validators, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { AppValidationHelpers } from 'src/app/app-validation-helpers';
import { AppValidators } from 'src/app/app-validators';
import { CountryCode, Language, UpdateUserAttributes, UpdateUserGQL, UserForUpdateGQL } from 'src/app/graphql/gql';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageSelectComponent } from 'src/app/shared/language-select/language-select.component';
import { ToastService } from 'src/app/toast/toast.service';
import { CountrySelectorComponent } from 'src/app/shared/country-selector/country-selector.component';
import { CountryStateSelectorComponent, CountryStateValue } from 'src/app/shared/country-state-selector/country-state-selector.component';

@Component({
  selector: 'app-edit-user',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FloatLabel,
    ButtonModule,
    AutoCompleteModule,
    SelectModule,
    PasswordModule,
    MessageModule,
    LanguageSelectComponent,
    CountrySelectorComponent,
    CountryStateSelectorComponent
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent {
  userId: string | null = null;

  titleOptions = [$localize`:user form|title mr:Mr`, $localize`:user form|title ms:Ms`];

  languageControl = new FormControl('', [Validators.required])

  form = new FormGroup({
    title: new FormControl(''),
    firstname: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    lastname: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    street: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    company: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    email: new FormControl('', [Validators.required, Validators.email]),
    zip: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(10)]),
    city: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    country: new FormControl('', [Validators.required]),
    // Holds the composite value of app-country-state-selector. Named after the server-side
    // field so remote validation errors on `countryState` still find this control.
    countryState: new FormControl<CountryStateValue>({ countryState: undefined, otherState: undefined }),
    phone: new FormControl('', [Validators.required, Validators.pattern(AppValidators.PHONE_REGEX)]),
    fax: new FormControl('', [Validators.pattern(AppValidators.PHONE_REGEX)]),
    login: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(255), Validators.pattern(AppValidators.LOGIN_REGEX)]),
    cellPhone: new FormControl('', [Validators.pattern(AppValidators.PHONE_REGEX)]),
    language: this.languageControl,
    commercialRegisterNumber: new FormControl(''),
    taxNumberCompany: new FormControl(''),
    idCardNumber: new FormControl(''),
    allowedIpAddresses: new FormControl('')
  })

  constructor(
    private activeRoute: ActivatedRoute,
    private userForUpdateGQL: UserForUpdateGQL,
    private updateUserGQL: UpdateUserGQL,
    private toastService: ToastService,
    private router: Router,
    private languageService: LanguageService
  ) {
    this.userId = this.activeRoute.snapshot.paramMap.get('userId');
    this.userForUpdateGQL.fetch({ id: this.userId! }).subscribe(result => {
      this.form.patchValue({
        ...result.data.user,
        // app-country-state-selector takes the composite value, not the raw ISO code.
        countryState: {
          countryState: result.data.user.countryState ?? undefined,
          otherState: result.data.user.otherState ?? undefined
        }
      })
      this.languageControl.setValue(result.data.user.settings?.language)
    })
  }

  getRemoteValidationMessages = AppValidationHelpers.getRemoteValidationMessages

  /** Selected country as the enum the country state selector expects. */
  get selectedCountry(): CountryCode | undefined {
    const country = this.form.controls.country.value;
    return country ? country as CountryCode : undefined;
  }

  onSubmit() {
    this.form.markAllAsDirty()
    if (this.form.valid) {
      const { countryState, ...attributes } = this.form.value;
      this.updateUserGQL.mutate({
        input: {
          userId: this.userId!,
          ...attributes,
          // countryState is a non-null String! server-side, so send '' for countries without states.
          countryState: countryState?.countryState ?? '',
          otherState: countryState?.otherState
        } as UpdateUserAttributes,
        language: this.languageService.getCurrentLanguage() as Language
      }).subscribe((result) => {
        switch(result.data?.updateUser.__typename) {
          case "ValidationErrors": {
            // An error whose attribute has no control in this form has nowhere to render, so show
            // it as a toast rather than failing silently. See BNDDEV-10062.
            const unmapped = AppValidationHelpers.applyValidationErrors(result.data.updateUser.validationMessages, this.form);
            const unmappedMessage = AppValidationHelpers.unmappedErrorMessage(unmapped);

            if (unmappedMessage !== null) {
              this.toastService.show('error', unmappedMessage);
            }

            break;
          }

          default:
            this.toastService.show('success', 'User updated successfully')
            this.router.navigateByUrl('/reseller/users')
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();
    }
  }

}
